import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Skeleton } from './ui/skeleton';
import logger from '../utils/logger';
import { useDebounce } from '../hooks/useDebounce';
import supabase from '../utils/supabaseClient';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ConsultantClientReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [assessments, setAssessments] = useState({ esa: [], emha: [] });
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [loadingClientId, setLoadingClientId] = useState(null); // Track which client is loading
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const subscriptionRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const config = {
        withCredentials: true
      };

      // Use the same endpoint as ConsultantDashboard (already optimized)
      const response = await axios.get(`${API_URL}/api/consultant/dashboard-data`, config);
      
      // Extract assigned_users from the consolidated response
      const assignedUsers = response.data.assigned_users || [];
      
      // Map assigned users to match the expected client structure
      const mappedClients = assignedUsers.map(user => ({
        id: user.id,
        client_id: user.id,
        client: {
          id: user.id,
          name: user.name,
          email: user.email,
          company: user.company,
          created_at: user.assigned_at
        },
        status: 'active',
        created_at: user.assigned_at
      }));
      
      setClients(mappedClients);
    } catch (err) {
      logger.error('Error fetching clients:', err);
      setError(err.response?.data?.detail || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  // Set up real-time subscription for consultant assignments
  useEffect(() => {
    if (!user?.id) return;

    // Check if Supabase is configured
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim() || '';
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim() || '';
    
    if (supabaseUrl && supabaseKey) {
      logger.log('🔌 Setting up real-time subscription for consultant assignments');
      
      // Subscribe to users table changes where consultant_id matches current consultant
      const channel = supabase
        .channel(`consultant_assignments_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*', // INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'users',
            filter: `consultant_id=eq.${user.id}`
          },
          (payload) => {
            logger.log('🔄 Consultant assignment update received:', payload);
            logger.log('   Event type:', payload.eventType);
            logger.log('   New data:', payload.new);
            logger.log('   Old data:', payload.old);
            
            // Refresh clients list when assignments change
            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
              logger.log('✅ New assignment detected, refreshing clients list...');
              fetchClients();
            } else if (payload.eventType === 'DELETE') {
              logger.log('🗑️ Assignment removed, refreshing clients list...');
              fetchClients();
            }
          }
        )
        .subscribe((status, err) => {
          logger.log('📡 Consultant assignments subscription status:', status);
          if (err) {
            logger.error('❌ Subscription error:', err);
          }
          if (status === 'SUBSCRIBED') {
            logger.log('✅ Subscribed to consultant assignment updates');
          } else if (status === 'CHANNEL_ERROR') {
            logger.warn('⚠️ Failed to subscribe to consultant assignments. Will use polling fallback.');
            // Fall back to polling if subscription fails
            startPolling();
          }
        });
      
      subscriptionRef.current = channel;
    } else {
      logger.warn('⚠️ Supabase not configured. Using polling for consultant assignments.');
      startPolling();
    }

    // Fallback polling mechanism (runs every 10 seconds)
    function startPolling() {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      pollingIntervalRef.current = setInterval(() => {
        logger.log('🔄 Polling for consultant assignment updates...');
        fetchClients();
      }, 10000); // Poll every 10 seconds
    }

    // Start polling as backup even if subscription is active
    startPolling();

    // Cleanup
    return () => {
      logger.log('🔌 Cleaning up consultant assignments subscription');
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [user?.id, fetchClients]);

  // Initial fetch
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const fetchClientAssessments = useCallback(async (clientId) => {
    // Don't refetch if already selected and loading
    if (selectedClient === clientId && loadingAssessments) {
      return;
    }

    try {
      setLoadingAssessments(true);
      setLoadingClientId(clientId); // Set loading client ID
      setError('');
      setSuccess('');

      const config = {
        withCredentials: true
      };

      const response = await axios.get(
        `${API_URL}/api/consultant/clients/${clientId}/assessments`,
        config
      );

      setAssessments({
        esa: response.data.esa_assessments || [],
        emha: response.data.emha_assessments || []
      });
      setSelectedClient(clientId);
    } catch (err) {
      logger.error('Error fetching assessments:', err);
      setError(err.response?.data?.detail || 'Failed to load client assessments');
    } finally {
      setLoadingAssessments(false);
      setLoadingClientId(null); // Clear loading client ID
    }
  }, [selectedClient, loadingAssessments]);

  const downloadWhiteLabelReport = async (assessmentId, reportType) => {
    try {
      setDownloadingId(assessmentId);
      setError('');
      setSuccess('');

      const config = {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true,
        responseType: 'blob'
      };

      const response = await axios.post(
        `${API_URL}/api/reports/consultant/generate-white-label-report`,
        {
          assessment_id: assessmentId,
          report_type: reportType
        },
        config
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `white_label_report_${assessmentId}.pdf`;
      if (contentDisposition) {
        // Match filename with or without quotes, and remove any trailing characters
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          // Remove quotes and trim whitespace
          filename = filenameMatch[1].replace(/['"]/g, '').trim();
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('White-labeled report downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logger.error('Error downloading report:', err);
      setError(err.response?.data?.detail || 'Failed to download white-labeled report');
    } finally {
      setDownloadingId(null);
    }
  };

  // Memoize filtered clients list - must be at top level, not in JSX
  // Use debounced search to avoid filtering on every keystroke
  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      if (!debouncedSearchQuery.trim()) return true;
      const query = debouncedSearchQuery.toLowerCase();
      const name = (client.client?.name || '').toLowerCase();
      const email = (client.client?.email || '').toLowerCase();
      const company = (client.client?.company || '').toLowerCase();
      return name.includes(query) || email.includes(query) || company.includes(query);
    });
  }, [clients, debouncedSearchQuery]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header Skeleton */}
          <div className="mb-8">
            <Skeleton className="h-10 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Clients List Skeleton */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="p-4">
                      <Skeleton className="h-5 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Assessments Placeholder Skeleton */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12">
                <Skeleton className="h-16 w-16 rounded-full mx-auto mb-4" />
                <Skeleton className="h-4 w-64 mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header with Back Button */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link
              to="/consultant/dashboard"
              className="mr-4 p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
            <div className="flex-1 flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Client Reports
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Download white-labeled reports for your client's assessments
                </p>
              </div>
              <Link
                to="/consultant/branding"
                className="px-4 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors"
              >
                Configure Branding
              </Link>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-green-800 dark:text-green-300">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Clients List */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Your Clients ({clients.length})
                </h2>
                {/* Search Bar */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search clients..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              
              {/* Filtered Clients */}
              {clients.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    No clients found
                  </p>
                  <Link
                    to="/consultant/dashboard"
                    className="text-emerald-600 dark:text-emerald-400 hover:underline"
                  >
                    Go to Dashboard
                  </Link>
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    No clients found matching "{debouncedSearchQuery}"
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
                  {filteredClients.map((client) => {
                    const isSelected = selectedClient === client.client_id;
                    const isLoading = loadingClientId === client.client_id;
                    
                    return (
                      <button
                        key={client.id}
                        onClick={() => fetchClientAssessments(client.client_id)}
                        disabled={isLoading}
                        className={`w-full p-4 text-left transition-colors ${
                          isSelected
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 border-l-4 border-emerald-500'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        } ${
                          isLoading ? 'opacity-75 cursor-wait' : 'cursor-pointer'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900 dark:text-white truncate">
                                {client.client?.name || 'Unknown'}
                              </p>
                              {isLoading && (
                                <svg className="animate-spin h-4 w-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {client.client?.email || 'No email'}
                            </p>
                            {client.status && (
                              <span className="inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                {client.status}
                              </span>
                            )}
                          </div>
                          {isSelected && !isLoading && (
                            <svg className="h-5 w-5 text-emerald-500 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Assessments List */}
          <div className="lg:col-span-2">
            {!selectedClient ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                <svg className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-600 dark:text-gray-400">
                  Select a client to view their assessments
                </p>
              </div>
            ) : loadingAssessments ? (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <Skeleton className="h-6 w-48" />
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-4">
                        <Skeleton className="h-6 w-full mb-2" />
                        <Skeleton className="h-4 w-2/3 mb-2" />
                        <Skeleton className="h-4 w-1/3" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* ESA Assessments */}
                {assessments.esa.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        ESA Assessments ({assessments.esa.length})
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {assessments.esa.map((assessment) => (
                        <div key={assessment.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                                {assessment.facility_name || 'ESA Assessment'}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                Industry: {assessment.industry_name || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-500">
                                Completed: {formatDate(assessment.completion_date || assessment.created_at)}
                              </p>
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => downloadWhiteLabelReport(assessment.id, 'esa_basic')}
                                disabled={downloadingId === assessment.id}
                                className="px-4 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                              >
                                {downloadingId === assessment.id ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Downloading...
                                  </span>
                                ) : (
                                  'Download White-Label'
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* EMHA Assessments */}
                {assessments.emha.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        EMHA Assessments ({assessments.emha.length})
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {assessments.emha.map((assessment) => (
                        <div key={assessment.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                                {assessment.facility_name || 'EMHA Assessment'}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {assessment.assessment_type || 'EMHA'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-500">
                                Completed: {formatDate(assessment.completion_date || assessment.created_at)}
                              </p>
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => downloadWhiteLabelReport(assessment.id, 'emha_basic')}
                                disabled={downloadingId === assessment.id}
                                className="px-4 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                              >
                                {downloadingId === assessment.id ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Downloading...
                                  </span>
                                ) : (
                                  'Download White-Label'
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Assessments */}
                {assessments.esa.length === 0 && assessments.emha.length === 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <svg className="h-16 w-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-600 dark:text-gray-400">
                      This client has no completed assessments yet
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                About White-Labeled Reports
              </h4>
              <p className="text-xs text-blue-800 dark:text-blue-400">
                White-labeled reports will include your company name, logo, and custom branding configured in your branding settings. 
                Make sure to configure your branding before downloading reports for your clients.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantClientReports;
